import { useMutation } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import { DisableTotpEnableEmail } from 'Apollo/mutations/person/DisableTotpEnableEmail';
import Modal from 'Components/Shared/Modal';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { useCallback, useState } from 'react';

interface MfaTransferTotpEmailProps {
  readonly userImmutableId: string;
  readonly userEmail: string;
  readonly refreshUserProfile: () => Promise<void>;
}

export function MfaTransferTotpEmail({
  userImmutableId,
  userEmail,
  refreshUserProfile
}: MfaTransferTotpEmailProps) {
  let [isOpen, setIsOpen] = useState(false);
  let ts = useTranslate();
  const { onError, onSuccess } = useNotification();

  let closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  let onSuccessSubmit = useCallback(async () => {
    onSuccess('page.userProfile.security.mfa.transfertotpemail.success', [
      userEmail
    ]);
    closeModal();
    await refreshUserProfile();
  }, [userEmail, closeModal, onSuccess, refreshUserProfile]);

  const [disableTotpEnableEmail] = useMutation<
    DisableTotpEnableEmailData,
    DisableTotpEnableEmailVariables
  >(DisableTotpEnableEmail, { onError, onCompleted: onSuccessSubmit });

  const handleSubmit = async () => {
    await disableTotpEnableEmail({
      variables: { immutableId: userImmutableId }
    });
  };

  return (
    <>
      <Button color="primary" onClick={() => setIsOpen(true)}>
        {ts('page.userProfile.security.mfa.transfertotpemail.title')}
      </Button>
      <Modal
        isOpen={isOpen}
        title="page.userProfile.security.mfa.transfertotpemail.dialog.title"
        maxWidth="md"
        minHeight={200}
        onClose={closeModal}
      >
        <Box display="flex" flexDirection="column" gap={2} alignItems="flex-end">
          <Typography>
            {ts(
              'page.userProfile.security.mfa.transfertotpemail.dialog.description',
              [userEmail]
            )}
          </Typography>
          <Box display="flex" gap={2}>
            <Button onClick={closeModal} color="inherit">
              {ts('common.action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              {ts('common.action.confirm')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
